/**
 * By default, React DOM escapes any values embedded in JSX before rendering them,
 * but sometimes it is necessary to sanitize the user-generated content of received entities.
 * If you use this data in component props without any sanitization or encoding,
 * it might create XSS vulnerabilities.
 *
 * You should especially consider how you are using extended data inside the app.
 */

const ESCAPE_TEXT_REGEXP = /[<>]/g;
const ESCAPE_TEXT_REPLACEMENTS = {
  //fullwidth lesser-than character
  '<': '\uff1c',
  //fullwidth greater-than character
  '>': '\uff1e',
};

// An example how you could sanitize text content.
// This swaps some coding related characters to less dangerous ones
const sanitizeText = str =>
  str == null
    ? str
    : typeof str === 'string'
    ? str.replace(ESCAPE_TEXT_REGEXP, ch => ESCAPE_TEXT_REPLACEMENTS[ch])
    : '';

// Enum and multi-enum work with predefined option configuration
const sanitizeEnum = (str, options) => (options.map(o => `${o.option}`).includes(str) ? str : null);
const sanitizeMultiEnum = (arr, options) =>
  Array.isArray(arr)
    ? arr.reduce((ret, value) => {
        const enumValue = sanitizeEnum(value, options);
        return enumValue ? [...ret, enumValue] : ret;
      }, [])
    : [];
const sanitizeLong = lng => (lng == null || typeof lng === 'number' ? lng : null);
const sanitizeBoolean = bool => (bool == null || typeof bool === 'boolean' ? bool : null);

// URL sanitizer. This code is adapted from
// https://github.com/braintree/sanitize-url/
// <sanitizeUrl>
const INVALID_PROTOCOL_REGEXP = /^([^\w]*)(javascript|data|vbscript)/im;
const HTML_ENTITIES_REGEXP = /&#(\w+)(^\w|;)?/g;
const CTRL_CHARACTERS_REGEXP = /[\u0000-\u001F\u007F-\u009F\u2000-\u200D\uFEFF]/gim;
const URL_SCHEME_REGEXP = /^([^:]+):/gm;
const RELATIVE_FIRST_CHARACTERS = ['.', '/'];

function isRelativeUrlWithoutProtocol(url) {
  return RELATIVE_FIRST_CHARACTERS.indexOf(url[0]) > -1;
}

// adapted from https://stackoverflow.com/a/29824550/2601552
function decodeHtmlCharacters(str) {
  return str.replace(HTML_ENTITIES_REGEXP, (match, dec) => {
    return String.fromCharCode(dec);
  });
}

export function sanitizeUrl(url) {
  const sanitizedUrl = decodeHtmlCharacters(url || '')
    .replace(CTRL_CHARACTERS_REGEXP, '')
    .trim();

  if (!sanitizedUrl) {
    return 'about:blank';
  }

  if (isRelativeUrlWithoutProtocol(sanitizedUrl)) {
    return sanitizedUrl;
  }

  const urlSchemeParseResults = sanitizedUrl.match(URL_SCHEME_REGEXP);

  if (!urlSchemeParseResults) {
    return sanitizedUrl;
  }

  const urlScheme = urlSchemeParseResults[0];

  if (INVALID_PROTOCOL_REGEXP.test(urlScheme)) {
    return 'about:blank';
  }

  return sanitizedUrl;
}
// </sanitizeUrl>

/**
 * Sanitize user entity.
 * If you add public data, you should probably sanitize it here.
 * By default, React DOM escapes any values embedded in JSX before rendering them,
 * but if you use this data on props, it might create XSS vulnerabilities
 * E.g. you should sanitize and encode URI if you are creating links from public data.
 */
export const sanitizeUser = entity => {
  const { attributes, ...restEntity } = entity || {};
  const { profile, ...restAttributes } = attributes || {};
  const { bio, displayName, abbreviatedName, publicData, privateData, metadata } = profile || {};

  const sanitizePublicData = publicData => {
    // TODO: If you add public data, you should probably sanitize it here.
    return publicData ? { publicData } : {};
  };
  // `privateData` is author-only: the Marketplace API only ever returns it for
  // the current user (never for other users, even transaction counterparties),
  // so carrying it through here exposes nothing to third parties. We keep it so
  // the user can read/edit their own private fields (e.g. phoneNumber — see
  // docs/contact-info-privacy.md) from `state.user.currentUser`, mirroring how
  // `sanitizeListing` preserves a listing's `privateData` for its author.
  const sanitizePrivateData = privateData => {
    return privateData ? { privateData } : {};
  };
  const sanitizeMetadata = metadata => {
    // TODO: If you add user-generated metadata through Integration API,
    // you should probably sanitize it here.
    return metadata ? { metadata } : {};
  };

  const profileMaybe = profile
    ? {
        profile: {
          abbreviatedName: sanitizeText(abbreviatedName),
          displayName: sanitizeText(displayName),
          bio: sanitizeText(bio),
          ...sanitizePublicData(publicData),
          ...sanitizePrivateData(privateData),
          ...sanitizeMetadata(metadata),
        },
      }
    : {};
  const attributesMaybe = attributes ? { attributes: { ...profileMaybe, ...restAttributes } } : {};

  return { ...attributesMaybe, ...restEntity };
};

/**
 * Sanitize extended data against configuration (against schemaType)
 * @param {any} value Any JSON value
 * @param {object} config containing "schemaType"
 * @returns sanitized value or null
 */
const sanitizedListingFields = (value, config) => {
  const { schemaType, enumOptions } = config;
  const sanitized =
    schemaType === 'text'
      ? sanitizeText(value)
      : schemaType === 'enum'
      ? sanitizeEnum(value, enumOptions)
      : schemaType === 'multi-enum'
      ? sanitizeMultiEnum(value, enumOptions)
      : schemaType === 'long'
      ? sanitizeLong(value)
      : schemaType === 'boolean'
      ? sanitizeBoolean(value)
      : null;

  return sanitized;
};

/**
 * Some of the public data is configurable. This validates that data against the given config.
 * (The config paramter contains listingFields config.)
 *
 * NOTE: this does not handle nested JSON-like objects or other extra data,
 * but there's handling for string type content ('<' & '>' characters are replaced with full-width ones).
 *
 * @param {object} publicData
 * @param {*} config
 * @returns
 */
// Claves que nunca deben pasar por `sanitizedListingFields`. Si están en Console
// listingFields con un schemaType no contemplado (o sin schemaType), esa función
// devuelve `null` y borra el valor real al hidratar la ficha — el mismo fallo que
// ya tenía `priceRange`. Las caras piscina/jardín viven aquí: `hasGarden` sí puede
// (y debe) estar en Console para el índice de búsqueda, y aun así no puede anularse.
const PASSTHROUGH_PUBLIC_DATA_KEYS = new Set([
  'priceRange',
  'hasGarden',
  'gardenTitle',
  'gardenDescription',
  'gardenPriceRange',
  'gardenImageOrder',
  'gardenPoolUseExtra',
  'hiddenImageIds',
  'poolMonths',
  'gardenPriceDiscountPercent',
  // Espacios interiores (docs/indoor-spaces.md): un array de objetos que no se declara en Console.
  'indoorSpaces',
]);

const sanitizeConfiguredPublicData = (publicData, config = {}) => {
  const sanitizedConfiguredPublicData = Object.entries(publicData).reduce((sanitized, entry) => {
    const [key, value] = entry;
    if (PASSTHROUGH_PUBLIC_DATA_KEYS.has(key)) {
      return { ...sanitized, [key]: value };
    }
    const foundListingFieldConfig = config?.listingFields?.find(d => d.key === key);
    const sanitizedValue = ['listingType', 'transactionProcessAlias', 'unitType'].includes(key)
      ? sanitizeText(value)
      : foundListingFieldConfig
      ? sanitizedListingFields(value, foundListingFieldConfig)
      : typeof value === 'string'
      ? sanitizeText(value)
      : value;

    return {
      ...sanitized,
      [key]: sanitizedValue,
    };
  }, {});
  return sanitizedConfiguredPublicData;
};

/**
 * Sanitize listing entity.
 * If you add public data, you should probably sanitize it here.
 * By default, React DOM escapes any values embedded in JSX before rendering them,
 * but if you use this data on props, it might create XSS vulnerabilities
 * E.g. you should sanitize and encode URI if you are creating links from public data.
 */
export const sanitizeListing = (entity, config = {}) => {
  const { attributes, ...restEntity } = entity;
  const { title, description, publicData, ...restAttributes } = attributes || {};

  const sanitizeLocation = location => {
    const { address, building } = location || {};
    return { address: sanitizeText(address), building: sanitizeText(building) };
  };

  const sanitizePublicData = publicData => {
    // Here's an example how you could sanitize location and rules from publicData:
    // TODO: If you add public data, you should probably sanitize it here.
    const { location, ...restPublicData } = publicData || {};
    const locationMaybe = location ? { location: sanitizeLocation(location) } : {};
    const sanitizedConfiguredPublicData = sanitizeConfiguredPublicData(restPublicData, config);

    return publicData ? { publicData: { ...locationMaybe, ...sanitizedConfiguredPublicData } } : {};
  };

  const attributesMaybe = attributes
    ? {
        attributes: {
          title: sanitizeText(title),
          description: sanitizeText(description),
          ...sanitizePublicData(publicData),
          ...restAttributes,
        },
      }
    : {};

  return { ...attributesMaybe, ...restEntity };
};

/**
 * Sanitize entities if needed.
 * Remember to add your own sanitization rules for your extended data
 */
export const sanitizeEntity = (entity, config) => {
  const { type } = entity;
  switch (type) {
    case 'listing':
      return sanitizeListing(entity, config);
    case 'user':
      return sanitizeUser(entity);
    default:
      return entity;
  }
};

export const MESSAGE_MASK = '****-*****';

/**
 * ¿El contenido de un mensaje ya enviado lleva la máscara del sanitizer? Sirve para detectar, al
 * cargar la conversación, que en algún momento se intentaron pasar datos de contacto (el texto
 * enmascarado se guarda en el servidor, así que esto sobrevive a recargas de página).
 */
export const containsMaskedContactInfo = content =>
  typeof content === 'string' && content.includes(MESSAGE_MASK);

/**
 * Teléfonos “deletreados”: tokens = dígitos (una o más cifras: 6, 11, 606) o palabras 0–9.
 * Cifras en palabras: español, inglés y catalán (p. ej. quatre, seven, siete).
 * Mínimo 8 tokens (p. ej. "6 cuatro siete 8 11 6 nueve 6" = 9 cifras con "11" como bloque).
 * Separadores: espacio, saltos de línea, <br>.
 */
const SPANISH_ONE_DIGIT_WORDS = [
  // Español 0–9
  'cero',
  'uno',
  'dos',
  'tres',
  'cuatro',
  'cinco',
  'seis',
  'siete',
  'ocho',
  'nueve',
  // Inglés 0–9
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  // Catalán 0–9 (zero/dos/tres comparten forma con ES/EN; u=1, quatre, cinc…)
  'u',
  'quatre',
  'cinc',
  'sis',
  'set',
  'vuit',
  'nou',
].join('|');
const PHONE_SPELL_TOKEN = `(?:\\b(?:${SPANISH_ONE_DIGIT_WORDS})\\b|\\b\\d+\\b)`;
/** Espacio, \n, \r\n, tabs, etc. O etiquetas de salto en HTML. */
const PHONE_INTER_DIGIT_GAP = '(?:\\s|<br\\s*/?>)';
/**
 * Separadores “libres” que la gente intercala entre cifras para esquivar el filtro:
 * espacios, saltos de línea, puntos, comas, guiones, dos puntos, barras y <br>.
 * No incluye letras, así que un grupo de dígitos no “salta” por encima de palabras.
 */
const PHONE_SEPARATOR = '(?:[\\s.,;:/\\\\\\-]|<br\\s*/?>)';
/** 8+ tokens (1 + 7 repeticiones): cubre 9 cifras con algún bloque de 2 dígitos (ej. 11). */
const PHONE_SPELL_SEQUENCE = new RegExp(
  `${PHONE_SPELL_TOKEN}(?:${PHONE_INTER_DIGIT_GAP}+${PHONE_SPELL_TOKEN}){7,}`,
  'gi'
);

/**
 * Cifras escritas en palabras (0–9) pegadas o con separadores libres, SIN exigir frontera de
 * palabra entre cada una. Cubre el caso "cuatrocuatrocuatro…" y "cuatro-cuatro.cuatro…".
 * Umbral: 7+ palabras-cifra seguidas (un móvil ES tiene 9 cifras).
 */
const PHONE_SPELLED_DIGIT_WORD = `(?:${SPANISH_ONE_DIGIT_WORDS})`;
const PHONE_SPELLED_GLUED = new RegExp(
  `(?:${PHONE_SPELLED_DIGIT_WORD}${PHONE_SEPARATOR}{0,4}){7,}`,
  'gi'
);

/**
 * Densidad de dígitos: 9+ cifras separadas por separadores libres de forma arbitraria.
 * Cubre "444 444 44 4", "444-444-44-4", "44 44 44 44 4", "4.4.4.4.4.4.4.4.4"…
 * (cada hueco admite hasta 4 separadores para no “unir” números no relacionados).
 */
const PHONE_DIGIT_RUN = new RegExp(`\\d(?:${PHONE_SEPARATOR}{0,4}\\d){8,}`, 'gi');

/**
 * Oculta secuencias tipo teléfono: deletreadas (mezcla dígitos/palabras), palabras-cifra
 * pegadas o con separadores libres, y cadenas densas de dígitos troceadas con separadores.
 */
const maskSpelledPhoneSequences = text =>
  text
    .replace(PHONE_SPELL_SEQUENCE, MESSAGE_MASK)
    .replace(PHONE_SPELLED_GLUED, MESSAGE_MASK)
    .replace(PHONE_DIGIT_RUN, MESSAGE_MASK);

/** Correo estándar usuario@dominio.tld */
const EMAIL_STANDARD = /\b[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}\b/g;

/** Deletreado: "calle17 arroba gmail.com" */
const EMAIL_SPELLED_WITH_DOT = /\b[A-Za-z0-9._%+\-]+\s+arroba\s+[A-Za-z0-9._%+\-]+\s*\.\s*[A-Za-z0-9._%+\-]+\b/gi;

/** Deletreado: "calle17 arroba gmail punto com" */
const EMAIL_SPELLED_PUNTO_COM = /\b[A-Za-z0-9._%+\-]+\s+arroba\s+[A-Za-z0-9._%+\-]+\s+punto\s+com\b/gi;

const maskEmails = text =>
  text
    .replace(EMAIL_STANDARD, MESSAGE_MASK)
    .replace(EMAIL_SPELLED_WITH_DOT, MESSAGE_MASK)
    .replace(EMAIL_SPELLED_PUNTO_COM, MESSAGE_MASK);

export const sanitizeMessage = message => {
  if (message == null) {
    return message;
  }

  // 1) Correos enteros (usuario@dominio.tld y variantes “arroba … punto com”)
  message = maskEmails(message);

  // 2) Teléfonos deletreados y con espacios / <br>
  message = maskSpelledPhoneSequences(message);

  // 2b) Tiradas mixtas de dígitos + palabras-número (validadas por nº de cifras reconstruidas)
  message = maskMixedNumericPhoneRuns(message);

  // 3) Patrones “estructurales”: números en bloque, URLs, @usuario, direcciones…
  const structuralPatterns = [
    /\b(\d{9})\b/, // 9 dígitos seguidos
    /\b(\d{3} \d{3} \d{3})\b/, // 123 456 789
    /\b(\d{3}-\d{3}-\d{3})\b/,
    /\b(\d{3}\.\d{3}\.\d{3})\b/,
    /\b(\d{3},\d{2},\d{2},\d{2})\b/,
    /\+\d{1,3}(?:[\s\-\.]+\d{1,4}){2,}/, // formato internacional: +33 6 21 34 17 56
    /\b(https?:\/\/[^\s]+)\b/i,
    /\b(www\.[^\s]+)\b/i,
    /\b(linkedin)\b/i,
    /(@\w+)/,
    /\b(calle\s+[a-záéíóú]+\s+\d{1,3})\b/i,
    /\b([a-záéíóú]+\s+\d{1,3},\s+\b[a-záéíóú]+\b\s+\d{1,5}\b\s+[a-záéíóú]+\b)\b/i,
  ];

  structuralPatterns.forEach(pattern => {
    message = message.replace(pattern, MESSAGE_MASK);
  });

  // 4) Dominios tipo “nombre.com” o “nombre punto com” (entero → máscara; antes que palabras sueltas)
  const domainLikePatterns = [
    // "Fincalosalmendros.com", "www.facebook.com", "Fincalosalmendros . com", "Fincalosalmendros .com"
    /\b(?:[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ_-]+\.)*[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ_-]+\s*\.\s*com\b/gi,
    // "Fincalosalmendros punto com"
    /\b[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ_-]+\s+punto\s+com\b/gi,
  ];

  domainLikePatterns.forEach(pattern => {
    message = message.replace(pattern, MESSAGE_MASK);
  });

  // 5) Palabras / marcas (orden: palabras largas antes que cortas para no dejar restos raros)
  const keywordPatterns = [
    /\b(alkipiscinas)\b/gi,
    // "alki piscinas", "alki-piscinas", "alki.piscinas"… (no enmascara "piscinas" a solas)
    /\balki(?:[\s.,;:/\\\-]+)piscinas\b/gi,
    /\b(instagram)\b/gi,
    /\b(facebook)\b/gi,
    /\b(whatsapp)\b/gi,
    /\b(gmail)\b/gi,
    /\b(google)\b/gi,
    /\b(insta)\b/gi, // no coincide con "instalaciones", "instalar", "instante" (\b tras "insta")
    /\b(whats)\b/gi,
    /\b(mail)\b/gi,
    /\b(arroba)\b/gi, // “arroba” = @ deletreado (tras enmascarar correos completos)
    /\b(web)\b/gi,
    /\b(ig)\b/gi,
  ];

  keywordPatterns.forEach(pattern => {
    message = message.replace(pattern, MESSAGE_MASK);
  });

  // 6) Cualquier @ restante (símbolo suelto o tras otros reemplazos)
  message = message.replace(/@/g, MESSAGE_MASK);

  return message;
};

/**
 * Detección de teléfonos repartidos en VARIOS mensajes ("seis trece" → "dos dos" → "uno uno"…).
 * Un filtro por-mensaje nunca puede pillar esto: cada fragmento es inofensivo por separado.
 * Aquí miramos el texto acumulado del mismo remitente para reconstruir el número.
 */

// Mapa palabra-número → dígitos (ES 0–15 + decenas, EN 0–10, CAT). Sirve para reconstruir
// la longitud del teléfono aunque venga dictado en palabras y troceado en varios mensajes.
const NUMBER_WORD_TO_DIGITS = {
  // Español 0–15
  cero: '0',
  uno: '1',
  dos: '2',
  tres: '3',
  cuatro: '4',
  cinco: '5',
  seis: '6',
  siete: '7',
  ocho: '8',
  nueve: '9',
  diez: '10',
  once: '11',
  doce: '12',
  trece: '13',
  catorce: '14',
  quince: '15',
  // Español decenas
  veinte: '20',
  vente: '20', // coloquial / mal escrito de "veinte" (dictado de teléfonos)
  treinta: '30',
  cuarenta: '40',
  cincuenta: '50',
  sesenta: '60',
  setenta: '70',
  ochenta: '80',
  noventa: '90',
  cien: '100',
  // Inglés 0–10
  zero: '0',
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
  ten: '10',
  // Catalán (los que no coinciden con ES/EN)
  u: '1',
  quatre: '4',
  cinc: '5',
  sis: '6',
  set: '7',
  vuit: '8',
  nou: '9',
  deu: '10',
};

// Conectores que la gente intercala al dictar números ("sesenta y uno").
const NUMBER_CONNECTORS = new Set(['y', 'i']);

/** Trocea un texto en tokens alfanuméricos (ignora separadores, <br>, puntuación…). */
const tokenizeForNumbers = text =>
  String(text || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .toLowerCase()
    .split(/[^0-9a-záéíóúüñ]+/i)
    .filter(Boolean);

/** ¿El token es “numérico”? (dígitos sueltos o palabra-número conocida) */
const isNumericToken = token => /^\d+$/.test(token) || NUMBER_WORD_TO_DIGITS[token] != null;

/** Nº de tokens numéricos en el texto. */
const numericTokenCount = text => tokenizeForNumbers(text).filter(isNumericToken).length;

/**
 * Reconstruye la cadena de dígitos del texto (dígitos literales + palabras-número) y
 * devuelve su longitud total. Ignora cualquier otra palabra, así que aguanta charla
 * intercalada entre los fragmentos del teléfono.
 */
const countPhoneDigits = text =>
  tokenizeForNumbers(text).reduce((digits, token) => {
    if (/^\d+$/.test(token)) return digits + token;
    if (NUMBER_WORD_TO_DIGITS[token] != null) return digits + NUMBER_WORD_TO_DIGITS[token];
    return digits;
  }, '').length;

/**
 * ¿El mensaje está compuesto SOLO de números (palabras o dígitos) + conectores?
 * Ej.: "seis trece", "dos dos", "uno siete dos", "444 444 44 4". Requiere ≥2 tokens numéricos
 * para no marcar respuestas legítimas de una sola cifra ("dos" a "¿cuántas personas?").
 */
const isNumericOnlyMessage = text => {
  const tokens = tokenizeForNumbers(text);
  if (tokens.length === 0) return false;
  const allNumericOrConnector = tokens.every(t => isNumericToken(t) || NUMBER_CONNECTORS.has(t));
  return allNumericOrConnector && tokens.filter(isNumericToken).length >= 2;
};

/** Longitud mínima (en dígitos) del texto acumulado para considerarlo un teléfono. */
const ACCUMULATED_PHONE_DIGITS_THRESHOLD = 9;

/**
 * Tirada contigua de tokens numéricos —dígitos (73, 11) Y/O palabras-número (seis, once, vente)—
 * separados solo por separadores libres y conectores "y"/"i". A diferencia de PHONE_SPELL_SEQUENCE,
 * que cuenta TOKENS (y exige 8), aquí validamos por longitud de dígitos RECONSTRUIDA, así que pillamos
 * mezclas como "Seis 73 siete siete 11 vente uno" (7 tokens → 9+ cifras) que antes se colaban.
 * Cualquier palabra no numérica corta la tirada, así que un texto con precios/medidas dispersos
 * (".. 30 euros, .. 50 euros ..") no la dispara.
 */
const PHONE_NUMBER_WORDS = Object.keys(NUMBER_WORD_TO_DIGITS)
  .sort((a, b) => b.length - a.length)
  .join('|');
const PHONE_RUN_TOKEN = `(?:\\d+|\\b(?:${PHONE_NUMBER_WORDS})\\b)`;
const PHONE_RUN_GAP = `(?:${PHONE_SEPARATOR}|\\b[yi]\\b)`;
const PHONE_MIXED_RUN = new RegExp(
  `${PHONE_RUN_TOKEN}(?:${PHONE_RUN_GAP}*${PHONE_RUN_TOKEN}){2,}`,
  'gi'
);

const maskMixedNumericPhoneRuns = text =>
  text.replace(PHONE_MIXED_RUN, run =>
    countPhoneDigits(run) >= ACCUMULATED_PHONE_DIGITS_THRESHOLD ? MESSAGE_MASK : run
  );

/**
 * Mensaje informativo largo (precios, medidas, fechas…) con muchas palabras no numéricas.
 * No debe disparar la regla de acumulado de cifras, que está pensada para teléfonos troceados.
 */
const hasSubstantialProse = text => {
  const tokens = tokenizeForNumbers(text);
  const numericCount = tokens.filter(isNumericToken).length;
  const nonNumericCount = tokens.length - numericCount;
  return nonNumericCount >= 15 || nonNumericCount >= numericCount * 3;
};

/**
 * ¿El acumulado de cifras parece un teléfono repartido en varios envíos y no un texto con
 * precios/medidas? Requiere fragmentos numéricos o un remitente que ya venía soltando números.
 */
const looksLikeSplitPhoneDrip = (message, priorMessages) => {
  if (isNumericOnlyMessage(message)) {
    return true;
  }
  const priorNumericTokens = priorMessages.reduce((sum, m) => sum + numericTokenCount(m), 0);
  if (priorNumericTokens === 0) {
    return false;
  }
  if (hasSubstantialProse(message)) {
    return false;
  }
  return true;
};

/**
 * Sanitiza un mensaje teniendo en cuenta los mensajes recientes del MISMO remitente en la
 * conversación, para detectar teléfonos/datos repartidos en varios envíos.
 *
 * @param {string} message Mensaje nuevo que se va a enviar.
 * @param {string[]} priorMessages Contenidos recientes del mismo remitente (ya filtrados por
 *   ventana de tiempo y autor por el llamador).
 * @returns {string} Mensaje sanitizado (enmascarado si forma parte de un teléfono troceado).
 */
export const sanitizeMessageWithHistory = (message, priorMessages = []) => {
  if (message == null) {
    return message;
  }

  const msgNumericTokens = numericTokenCount(message);

  // a) Fragmento "solo números": lo enmascaramos si por sí mismo ya tiene ≥3 cifras, o si tiene
  //    ≥2 y el remitente ya venía soltando números (no marcamos una respuesta numérica aislada).
  if (msgNumericTokens >= 1 && isNumericOnlyMessage(message)) {
    const priorNumericTokens = priorMessages.reduce((sum, m) => sum + numericTokenCount(m), 0);
    if (msgNumericTokens >= 3 || (msgNumericTokens >= 2 && priorNumericTokens > 0)) {
      return MESSAGE_MASK;
    }
  }

  // b) Acumulado: teléfono troceado en varios envíos. No enmascarar textos informativos con
  //    muchos precios/medidas (falso positivo si solo se suman cifras sueltas en un párrafo largo).
  if (msgNumericTokens >= 1 && looksLikeSplitPhoneDrip(message, priorMessages)) {
    const combined = [...priorMessages, message].join(' ');
    if (countPhoneDigits(combined) >= ACCUMULATED_PHONE_DIGITS_THRESHOLD) {
      return MESSAGE_MASK;
    }
  }

  return sanitizeMessage(message);
};
